import React, { ReactNode } from "react";
import { AdminService } from "src/services/AdminService";
import { iClinicProfile, iCommunityProfile, iPractitionerProfile, iProfile } from "ps-shared";

import Error from "src/components/General/Error";
import Loading from "src/components/General/Loading";
import { ProfileManageMode } from "src/models/ProfileManageMode";
import PublishProfile from "src/components/My/Profiles/PublishProfile";
import { navigate } from "gatsby";
import { Container } from "reactstrap";
import { PractitionerProfileWizard } from "./PractitionerProfileWizard";
import ClinicProfileWizard from "./ClinicProfileWizard";
import CommunityProfileWizard from "./CommunityProfileWizard";

export interface ProfileWizardCbData {
  values: any;
  page: number;
}
export interface ProfileWizardCbResult {
  success: boolean;
  profile: iClinicProfile | iCommunityProfile | iPractitionerProfile;
  errs: string[];
}
interface ProfileWizardProps {
  // passed in from the private route on manage page
  slug: string;
  newProfileType: string;
}
interface ProfileWizardState {
  mode: ProfileManageMode;
  profileType: string;
  forceEdit: boolean;
  errored: boolean;
  loading: boolean;
  profile: iClinicProfile | iCommunityProfile | iPractitionerProfile;
}

export class ProfileWizard extends React.Component<ProfileWizardProps, ProfileWizardState> {
  constructor(props: ProfileWizardProps) {
    super(props);
    const mode = this.getMode();
    this.state = {
      mode,
      // if the mode is new (ie the slug on the route is new-practitioner) then the route contains the profile type
      profileType: mode === ProfileManageMode.new ? this.props.newProfileType : undefined,
      forceEdit: false,
      errored: false,
      profile: null,
      loading: false,
    };
    this.forceEditCb = this.forceEditCb.bind(this);
    this.wizardSubmitCb = this.wizardSubmitCb.bind(this);
    this.wizardNextCb = this.wizardNextCb.bind(this);
  }

  public componentDidMount(): void {
    this.setState({
      loading: true,
    });
    if (this.state.mode === "edit") {
      AdminService.getNetworkProfile(this.props.slug)
        .then((p) => {
          return this.setState({
            profile: p.profile,
            loading: false,
          });
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      this.setState({
        loading: false,
      });
    }
  }

  public getMode(): ProfileManageMode {
    if (this.props.newProfileType) {
      // this is a 'new' mode
      return ProfileManageMode.new;
    }
    return ProfileManageMode.edit;
  }

  public forceEditCb(): void {
    this.setState({
      forceEdit: true,
    });
  }

  public wizardNextCb(data: ProfileWizardCbData): Promise<ProfileWizardCbResult> {
    return new Promise((resolve, reject) => {
      if (this.state.mode === ProfileManageMode.edit || data.page > 0) {
        // save
        AdminService.updateProfile(data.values, this.state.profile.slug)
          .then((result: any) => {
            this.setState({
              profile: result.profile as iClinicProfile | iCommunityProfile | iPractitionerProfile,
            });
            return resolve({
              success: true,
              profile: result.profile,
            } as ProfileWizardCbResult);
          })
          .catch((error) => {
            const result = {
              success: false,
            } as ProfileWizardCbResult;
            console.error(error);
            result.errs = error.response.data.err;
            reject(result);
          });
      } else {
        // create the profile
        AdminService.createProfile(data.values)
          .then((result) => {
            this.setState({
              mode: ProfileManageMode.edit, // now we're in 'edit' mode (we've created the profile)
              forceEdit: true, // keep the editing going on the next page
              profile: result.profile as iClinicProfile | iCommunityProfile | iPractitionerProfile,
            });
            return resolve({
              success: true,
              profile: result.profile,
            } as ProfileWizardCbResult);
          })
          .catch((error: any) => {
            const result = {
              success: false,
            } as ProfileWizardCbResult;
            if (error.response.status === 400) {
              result.errs = error.response.data.err;
            } else {
              this.setState({
                errored: true,
              });
            }
            reject(result);
          });
      }
    });
  }

  public wizardSubmitCb(data: ProfileWizardCbData): Promise<ProfileWizardCbResult> {
    return new Promise((resolve, reject) => {
      if (this.state.mode === ProfileManageMode.edit) {
        // update the profile
        AdminService.updateProfile(data.values, this.state.profile.slug)
          .then((result: any) => {
            if (this.state.profile.ver === "draft") {
              // send the user back to the view where we ask them to preview, edit or publish
              window.scrollTo({ top: 0, behavior: "smooth" });
              this.setState({
                forceEdit: false,
                profile: result.profile,
              });
            } else {
              navigate("/my/profiles/");
            }
            return resolve({
              success: true,
              profile: result.profile,
            } as ProfileWizardCbResult);
          })
          .catch((error: any) => {
            const result = {
              success: false,
            } as ProfileWizardCbResult;
            console.error(error);
            result.errs = error.response.data.err;
            reject(result);
          });
      } else {
        // create the profile
        AdminService.createProfile(data.values)
          .then((result) => {
            navigate(`/my/profiles/${result.profile.slug}/`, { replace: true });
            window.scrollTo({ top: 0, behavior: "smooth" });
            this.setState({
              mode: ProfileManageMode.edit,
              forceEdit: false,
              profile: result.profile as iClinicProfile | iCommunityProfile | iPractitionerProfile,
            });
            return resolve({
              success: true,
              profile: result.profile,
            } as ProfileWizardCbResult);
          })
          .catch((error: any) => {
            const result = {
              success: false,
            } as ProfileWizardCbResult;
            if (error.response.status === 400) {
              result.errs = error.response.data.err;
            } else {
              this.setState({
                errored: true,
              });
            }
            reject(result);
          });
      }
    });
  }

  public getNewProfileWizardComponent(profileType: string, mode: ProfileManageMode, profile: iProfile): ReactNode {
    if (profileType === "clinic") {
      return (
        <ClinicProfileWizard profile={profile as iClinicProfile} mode={mode} submitCb={this.wizardSubmitCb} nextCb={this.wizardNextCb} />
      );
    }
    if (profileType === "practitioner") {
      return (
        <PractitionerProfileWizard
          profile={profile as iPractitionerProfile}
          mode={mode}
          submitCb={this.wizardSubmitCb}
          nextCb={this.wizardNextCb}
        />
      );
    }
    if (profileType === "community") {
      return (
        <CommunityProfileWizard
          profile={profile as iCommunityProfile}
          mode={mode}
          submitCb={this.wizardSubmitCb}
          nextCb={this.wizardNextCb}
        />
      );
    }
    return <div>Invalid profile type!!</div>;
  }

  public getContent(): ReactNode {
    if (this.state.loading) {
      return <Loading />;
    }
    if (this.state.errored || this.state.errored) {
      return <Error />;
    }
    if (this.state.mode === "edit") {
      // if we're in draft mode, start out by asking the user if they are ready to publish
      if (this.state.profile?.ver?.toLowerCase() === "draft" && !this.state.forceEdit) {
        return <PublishProfile profile={this.state.profile} forceEditCb={this.forceEditCb} />;
      }
      return this.getNewProfileWizardComponent(this.state.profile?.profileType, this.state.mode, this.state.profile);
    }
    return this.getNewProfileWizardComponent(this.state.profileType, this.state.mode, {} as iProfile);
  }

  // tslint:disable:max-line-length
  public render(): ReactNode {
    return <Container>{this.getContent()}</Container>;
  }
}
